import React, { useState, useEffect, useRef } from "react";
import { connect, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {get, post, put} from "../../../../api";
import { getUsers } from "../../../../store/thunk/site";
import { Autocomplete, TextField } from "@mui/material";
import { formatDate } from "../../../../utils/dateFormat";
import { resolveSiteCheckPdfTarget } from "./shared/resolveSiteCheckPdfTarget";
import { toJavaLocalDateTime, toJavaLocalDate } from "./shared/siteCheckDateUtils";
import { calculateSiteCheckDueDate } from "../../../../utils/siteCheckRecurrence";
import SiteCheckBackButton from "./shared/SiteCheckBackButton";
import { getSiteCheckErrorMessage } from "./shared/siteCheckErrorMessage";
import { v4 as uuidv4 } from 'uuid';
import { saveAs } from 'file-saver';
import axios from 'axios';
import pdfTemplate from './pdf/AirConditioningCertificate.pdf';
import RiskScoreCard from "./RiskScoreCard";
import moment from "moment";

const getUkLocalDate = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const values = Object.fromEntries(
    parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value])
  );

  return `${values.year}-${values.month}-${values.day}`;
};

const getUserLabel = (user) => user?.name || user?.email || "";

const isActiveUser = (user) =>
  Boolean(user?.id) &&
  (!user?.status || String(user.status).toLowerCase() === "active");

const userBelongsToSite = (user, siteId) => {
  if (!user?.id || !siteId) return false;

  if (Number(user.defaultSiteId) === Number(siteId)) {
    return true;
  }

  return Boolean(
    user.taggedSites?.some(
      (site) => Number(site.id ?? site.siteId) === Number(siteId)
    )
  );
};

const uniqueUsersById = (userList) => {
  const uniqueUsers = new Map();

  userList.filter(Boolean).forEach((user) => {
    if (user?.id && !uniqueUsers.has(String(user.id))) {
      uniqueUsers.set(String(user.id), user);
    }
  });

  return Array.from(uniqueUsers.values());
};

const sortUsersByName = (userList) =>
  [...userList].sort((a, b) =>
    getUserLabel(a).localeCompare(getUserLabel(b), undefined, { sensitivity: "base" })
  );

// Air Conditioning is used inside React 18 StrictMode and can be remounted
// immediately in development. Reuse only identical requests that are currently
// in flight; completed responses are not cached, so subsequent genuine reloads
// still read fresh data.
const airConditioningReadRequestsInFlight = new Map();

const getAirConditioningRead = (url) => {
  const existingRequest = airConditioningReadRequestsInFlight.get(url);
  if (existingRequest) {
    return existingRequest;
  }

  const request = get(url).finally(() => {
    if (airConditioningReadRequestsInFlight.get(url) === request) {
      airConditioningReadRequestsInFlight.delete(url);
    }
  });

  airConditioningReadRequestsInFlight.set(url, request);
  return request;
};

let airConditioningUsersRequestInFlight = null;

const loadAirConditioningUsers = (loadUsers) => {
  if (airConditioningUsersRequestInFlight) {
    return airConditioningUsersRequestInFlight;
  }

  const request = Promise.resolve(loadUsers()).finally(() => {
    if (airConditioningUsersRequestInFlight === request) {
      airConditioningUsersRequestInFlight = null;
    }
  });

  airConditioningUsersRequestInFlight = request;
  return request;
};

const getMostRecentGenericInspection = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  return items.reduce((latest, current) => {
    if (!latest) {
      return current;
    }

    const latestCreatedAt = Date.parse(latest.createdAt || "");
    const currentCreatedAt = Date.parse(current.createdAt || "");

    if (Number.isFinite(currentCreatedAt) && Number.isFinite(latestCreatedAt)) {
      if (currentCreatedAt !== latestCreatedAt) {
        return currentCreatedAt > latestCreatedAt ? current : latest;
      }
    } else if (Number.isFinite(currentCreatedAt)) {
      return current;
    } else if (Number.isFinite(latestCreatedAt)) {
      return latest;
    }

    return Number(current.id || 0) > Number(latest.id || 0) ? current : latest;
  }, null);
};

let PDFLib;

if (typeof window !== 'undefined') {
  import('pdf-lib').then((pdfLib) => {
    PDFLib = pdfLib;
  });
}

// Helper function to fetch PDF as ArrayBuffer
const fetchPdfTemplate = async () => {
  try {
    const response = await fetch(pdfTemplate);
    if (!response.ok) {
      throw new Error('Failed to load PDF template: ' + response.statusText);
    }
    const arrayBuffer = await response.arrayBuffer();
    const header = new Uint8Array(arrayBuffer, 0, 5);
    const headerStr = String.fromCharCode.apply(null, header);
    if (headerStr !== '%PDF-') {
      throw new Error('Invalid PDF file: Missing PDF header');
    }
    return arrayBuffer;
  } catch (error) {
    throw new Error('Failed to load PDF template: ' + error.message);
  }
};

const AirConditioning = ({
                           sasToken,
                           checkId,
                           subType,
                           category,
                           siteCheck,
                           users,
                           getUsers,
                           siteSelectedForGlobal,
                           loggedInUserData,
                           embedded = false,
                         }) => {
  const [formData, setFormData] = useState({
    address: "",
    assetId: "",
    siteContact: "",
    inspectionDate: getUkLocalDate(),
    siteContactNo: "",
    job: "",
    manufacturer: "",
    modelNumber: "",
    position: "",
    floor: "",
    room: "",
    serialNumber: "",
    assetName:"",
    report: "",
    param1: "", // jobComplete
    param2: "", // partsRequired
    param3: "", // fGasCheck
    param4: "", // filtersCleaned
    param5: "", // indoorCoilCleaned
    param6: "", // outdoorCoilCleaned
    param7: "", // systemLeakCheck
    param8: "", // drainPumpTest
    param9: "", // electricalConnectionsCheck
    param10: "", // temperatureChecks
    param1Remark: "", // ofn
    param2Remark: "", // welding
    param3Remark: "", // legacy refrigerant field retained for historical records
    refrigerantType: "",
    refrigerantQuantity: "",
    param4Remark: "", // reclaimCylinder
    param5Remark: "", // cleaningChemicals
    param6Remark: "", // airSpray
    client: "",
    user: loggedInUserData || {},
    engineer: loggedInUserData?.id || "",
    selectedAsset: null,
    signedDate: getUkLocalDate(),
    clientUser: null,
    siteContactUser: null,
    actionId: null,
  });

  const sites = useSelector((state) => state.site.sites);
  const usersRef = useRef(users);
  usersRef.current = users;
  const [siteCheckAssets, setSiteCheckAssets] = useState([]);
  const [siteCheckSite, setSiteCheckSite] = useState(null);
  const authoritativeSiteId = siteCheck?.siteId ? Number(siteCheck.siteId) : null;
  const authoritativeSite =
      siteCheckSite ||
      sites.find((site) => Number(site.siteId ?? site.id) === authoritativeSiteId) ||
      (Number(siteSelectedForGlobal?.siteId ?? siteSelectedForGlobal?.id) === authoritativeSiteId
          ? siteSelectedForGlobal
          : null);
  const authoritativeSiteName =
      authoritativeSite?.siteName || authoritativeSite?.name ||
      (authoritativeSiteId ? `site ${authoritativeSiteId}` : "the Site Check site");
  const wrongSiteAssetMessage =
      `The selected asset does not belong to ${authoritativeSiteName}. Please select an asset from the correct site.`;
  const authoritativeSiteRef = useRef(authoritativeSite);
  authoritativeSiteRef.current = authoritativeSite;

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showPdfButton, setShowPdfButton] = useState(false);
  const [generatedPdfBlob, setGeneratedPdfBlob] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [pendingHistoryRetry, setPendingHistoryRetry] = useState(null);
  const [isRetryingHistory, setIsRetryingHistory] = useState(false);
  const [inspectionDetails, setInspectionDetails] = useState(null);
  const [lastEngineerId, setLastEngineerId] = useState(null);
  const [selectedEngineerProfile, setSelectedEngineerProfile] = useState(null);

    const [folderIds, setFolderIds] = useState({
    logBooks: null,
    EnvironmentalLogBook: null,
    airConditioning: null
  });
  const [checkStatus, setCheckStatus] = useState('Open');
  const [isFormEditable, setIsFormEditable] = useState(true);
  const [currentCheckId, setCurrentCheckId] = useState(checkId || null);
  const [showRiskAssessment, setShowRiskAssessment] = useState(false);
  const [actionRaised, setActionRaised] = useState(false);
  const [existingAction, setExistingAction] = useState(null);
  const navigate = useNavigate();

  const isInternalUserTaggedWithSite = true;

  const [popup, setPopup] = useState({
    show: false,
    content: "",
    position: { x: 0, y: 0 },
  });

  const selectableEngineers = sortUsersByName(uniqueUsersById([
    ...(users || []).filter(
      (user) =>
        isActiveUser(user) && userBelongsToSite(user, authoritativeSiteId)
    ),
    ...(isActiveUser(loggedInUserData) ? [loggedInUserData] : []),
  ]));

  const loggedInEngineerOption = selectableEngineers.find(
    (user) => String(user.id) === String(loggedInUserData?.id)
  );
  const leadEngineerOption = uniqueUsersById([
    ...(users || []),
    ...selectableEngineers,
  ]).find(
    (user) =>
      isActiveUser(user) &&
      String(user.id) === String(siteCheck?.leadUserID) &&
      String(user.id) !== String(loggedInUserData?.id)
  );

  const engineerOptions = checkStatus === "Open"
    ? uniqueUsersById([
        loggedInEngineerOption,
        leadEngineerOption,
        ...selectableEngineers,
      ])
    : uniqueUsersById([
        ...(formData.user?.id ? [formData.user] : []),
        ...selectableEngineers,
      ]);

  const selectedEngineer = engineerOptions.find(
    (user) => String(user.id) === String(formData.engineer)
  ) || null;

  // The signature uploaded on Edit Profile is persisted on the full user
  // details record (`user.signature`).  The general /api/user/all response is
  // not treated as the authoritative signature source, so load the selected
  // engineer's profile whenever the engineer changes.
  useEffect(() => {
    let cancelled = false;
    const engineerId = formData.engineer;

    if (!engineerId) {
      setSelectedEngineerProfile(null);
      return undefined;
    }

    const loadEngineerProfile = async () => {
      try {
        const response = await getAirConditioningRead(`/api/user/${engineerId}/details`);
        if (!cancelled) {
          setSelectedEngineerProfile(response?.user || response || null);
        }
      } catch (error) {
        if (!cancelled) {
          setSelectedEngineerProfile(null);
        }
      }
    };

    loadEngineerProfile();

    return () => {
      cancelled = true;
    };
  }, [formData.engineer]);

  const engineerSignatureUrl =
    selectedEngineerProfile?.signature ||
    selectedEngineer?.signature ||
    (String(formData.user?.id || "") === String(formData.engineer || "")
      ? formData.user?.signature
      : "") ||
    "";

  const selectedAsset = siteCheckAssets.find(
      (asset) => Number(asset.assetId) === Number(formData.assetId)
  );
  const handleMouseEnter = (e, content) => {
    if (!content) return;
    setPopup({
      show: true,
      content,
      position: {
        x: e.target.getBoundingClientRect().left,
        y: e.target.getBoundingClientRect().top - 10,
      },
    });
  };

  const handleMouseLeave = () => {
    setPopup((prev) => ({ ...prev, show: false }));
  };

  useEffect(() => {
    setSiteCheckAssets([]);
    setSiteCheckSite(null);
    setLastEngineerId(null);
    setFormData((prev) => ({
      ...prev,
      assetId: "",
      selectedAsset: null,
      manufacturer: "",
      modelNumber: "",
      position: "",
      floor: "",
      room: "",
      serialNumber: "",
      assetName: "",
      engineer: siteCheck?.status === "Done" ? "" : (loggedInUserData?.id || ""),
      user: siteCheck?.status === "Done" ? {} : (loggedInUserData || {}),
    }));
  }, [
    checkId,
    authoritativeSiteId,
    siteCheck?.status,
    loggedInUserData?.id,
    loggedInUserData?.name,
  ]);

  useEffect(() => {
    if (checkStatus !== "Open" || !loggedInUserData?.id) {
      return;
    }

    setFormData((prev) => {
      if (
        prev.engineer &&
        String(prev.engineer) !== String(loggedInUserData.id)
      ) {
        return prev;
      }

      const loggedInEngineer =
        selectableEngineers.find(
          (user) => String(user.id) === String(loggedInUserData.id)
        ) || loggedInUserData;

      if (
        String(prev.engineer || "") === String(loggedInEngineer.id) &&
        prev.user?.name === loggedInEngineer.name
      ) {
        return prev;
      }

      return {
        ...prev,
        engineer: loggedInEngineer.id,
        user: loggedInEngineer,
      };
    });
  }, [
    checkId,
    checkStatus,
    loggedInUserData?.id,
    loggedInUserData?.name,
    users.length,
  ]);

  const fetchInspectionData = async (
      siteCheckStatus = checkStatus,
      assetsForSite = siteCheckAssets,
      preserveOpenFormValues = false,
      usersForInspection = users,
      isCancelled = () => false
  ) => {
    try {
      if (!checkId || isCancelled()) {
        return { hasLinkedAction: false, mostRecentItem: null };
      }

      const apiData = await getAirConditioningRead(`/api/site-check/generic-inspection/${checkId}`);
      if (isCancelled()) {
        return { hasLinkedAction: false, mostRecentItem: null };
      }

      if (apiData && apiData.length > 0) {
        // The API returns all Generic Inspection rows for this checkId without
        // a guaranteed order. Select the newest record explicitly.
        const mostRecentItem = getMostRecentGenericInspection(apiData);
        const inspectionUsers = Array.isArray(usersForInspection) ? usersForInspection : [];
        const restoredAsset = assetsForSite.find(
            (asset) =>
                Number(asset.assetId) === Number(mostRecentItem.assetId) &&
                Number(asset.siteId) === authoritativeSiteId
        );

        const clientUser = inspectionUsers.find(
            (user) => user.id === mostRecentItem.client
        );
        const engineerUser = inspectionUsers.find(
            (user) => String(user.id) === String(mostRecentItem.engineer)
        );
        const siteContactUser = inspectionUsers.find(
            (user) => user.id === mostRecentItem.siteContact
        );
        const currentOpenDate = getUkLocalDate();
        const isCurrentOpenInspection =
            siteCheckStatus === "Open" &&
            String(mostRecentItem.inspectionDate || "").slice(0, 10) === currentOpenDate;
        const openInspectionEngineer = isCurrentOpenInspection
            ? (engineerUser || loggedInUserData || {})
            : (loggedInUserData || {});

        let linkedAction = null;
        if (mostRecentItem.actionId) {
          linkedAction = await fetchActionById(mostRecentItem.actionId);
          if (isCancelled()) {
            return { hasLinkedAction: false, mostRecentItem };
          }
        }

        if (isCancelled()) {
          return { hasLinkedAction: false, mostRecentItem };
        }

        setLastEngineerId(mostRecentItem.engineer || null);
        if (linkedAction) {
          setExistingAction(linkedAction);
          setActionRaised(true);
        }

        setFormData((prev) => ({
          ...prev,
          address: prev.address,
          assetId: restoredAsset?.assetId || "",
          siteContact: mostRecentItem.siteContact || prev.siteContact,
          inspectionDate: siteCheckStatus === "Open"
              ? (preserveOpenFormValues ? prev.inspectionDate : getUkLocalDate())
              : (mostRecentItem.inspectionDate || prev.inspectionDate),
          siteContactNo: mostRecentItem.siteContactNo || prev.siteContactNo,
          job: mostRecentItem.job || prev.job,
          manufacturer: restoredAsset ? (mostRecentItem.manufacturer || restoredAsset.manufacturer || "") : "",
          modelNumber: restoredAsset ? (mostRecentItem.modelNumber || restoredAsset.model || "") : "",
          position: restoredAsset ? (mostRecentItem.position || restoredAsset.position || "") : "",
          floor: restoredAsset ? (mostRecentItem.floor || restoredAsset.floor || "") : "",
          room: restoredAsset ? (mostRecentItem.room || restoredAsset.room || "") : "",
          assetName: restoredAsset ? (mostRecentItem.assetName || restoredAsset.assetName || "") : "",
          serialNumber: restoredAsset ? (mostRecentItem.serialNumber || restoredAsset.serialNumber || "") : "",
          report: mostRecentItem.report || prev.report,
          param1: mostRecentItem.param1 || prev.param1,
          param2: mostRecentItem.param2 || prev.param2,
          param3: mostRecentItem.param3 || prev.param3,
          param4: mostRecentItem.param4 || prev.param4,
          param5: mostRecentItem.param5 || prev.param5,
          param6: mostRecentItem.param6 || prev.param6,
          param7: mostRecentItem.param7 || prev.param7,
          param8: mostRecentItem.param8 || prev.param8,
          param9: mostRecentItem.param9 || prev.param9,
          param10: mostRecentItem.param10 || prev.param10,
          param1Remark: mostRecentItem.param1Remark || prev.param1Remark,
          param2Remark: mostRecentItem.param2Remark || prev.param2Remark,
          // Keep the legacy value visible for historical records. Only values
          // that are already an exact known refrigerant type are used as a
          // backward-compatible type fallback; ambiguous legacy values such as
          // "Type" or "Quant" are never guessed or migrated in the UI.
          param3Remark: mostRecentItem.param3Remark || prev.param3Remark,
          refrigerantType:
              mostRecentItem.refrigerantType ||
              (["R410A", "R32"].includes(String(mostRecentItem.param3Remark || "").trim().toUpperCase())
                  ? String(mostRecentItem.param3Remark).trim().toUpperCase()
                  : prev.refrigerantType),
          refrigerantQuantity:
              mostRecentItem.refrigerantQuantity !== null &&
              mostRecentItem.refrigerantQuantity !== undefined
                  ? String(mostRecentItem.refrigerantQuantity)
                  : prev.refrigerantQuantity,
          param4Remark: mostRecentItem.param4Remark || prev.param4Remark,
          param5Remark: mostRecentItem.param5Remark || prev.param5Remark,
          param6Remark: mostRecentItem.param6Remark || prev.param6Remark,
          client: mostRecentItem.client || "",
          engineer: siteCheckStatus === "Open"
              ? (preserveOpenFormValues
                  ? (prev.engineer || loggedInUserData?.id || "")
                  : (isCurrentOpenInspection
                      ? (mostRecentItem.engineer || loggedInUserData?.id || "")
                      : (loggedInUserData?.id || "")))
              : (mostRecentItem.engineer || prev.engineer || ""),
          user: siteCheckStatus === "Open"
              ? (preserveOpenFormValues ? (prev.user || loggedInUserData || {}) : openInspectionEngineer)
              : (engineerUser || prev.user || {}),
          selectedAsset: restoredAsset || null,
          signedDate: siteCheckStatus === "Open"
              ? (preserveOpenFormValues ? prev.signedDate : getUkLocalDate())
              : (mostRecentItem.signedDate || prev.signedDate),
          clientUser: clientUser || null,
          siteContactUser: siteContactUser || null,
          actionId: mostRecentItem.actionId || null,
        }));

        return {
          hasLinkedAction: Boolean(linkedAction),
          mostRecentItem,
        };
      }

      if (!isCancelled()) {
        setLastEngineerId(null);
      }
      return { hasLinkedAction: false, mostRecentItem: null };
    } catch {
      // Failed to load inspection data — form remains editable with defaults.
      return { hasLinkedAction: false, mostRecentItem: null };
    }
  };
  const fetchActionById = async (id) => {
    try {
      if (!id) return null;
      const response = await getAirConditioningRead(`/api/site/actions/id/${id}`);
      return response;
    } catch {
      return null;
    }
  };

  const fetchExistingActions = async (
      checkIdOverride = currentCheckId,
      actionIdOverride = formData.actionId,
      isCancelled = () => false
  ) => {
    try {
      const targetCheckId = checkIdOverride;

      if (actionIdOverride) {
        const action = await fetchActionById(actionIdOverride);
        if (isCancelled()) return;

        if (action && action.checkId === targetCheckId) {
          setExistingAction(action);
          setActionRaised(true);
          return;
        }

        if (!isCancelled()) {
          setFormData((prev) => ({ ...prev, actionId: null }));
        }
      }

      if (!authoritativeSiteId || !targetCheckId || isCancelled()) return;

      const response = await getAirConditioningRead(`/api/site/actions/${authoritativeSiteId}`);
      if (isCancelled()) return;

      if (response && response.length > 0) {
        const relevantActions = response.filter(
            (action) => action.checkId === targetCheckId
        );

        if (relevantActions.length > 0) {
          const mostRecentAction = relevantActions.sort(
              (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )[0];

          setExistingAction(mostRecentAction);
          setActionRaised(true);
          setFormData((prev) => ({
            ...prev,
            actionId: mostRecentAction.actionId,
          }));
        }
      }
    } catch {
      // Non-blocking — form can still be submitted without linked action.
    }
  };

  const fetchFolderStructure = async (siteId, isCancelled = () => false) => {
    try {
      const parentFoldersResponse = await getAirConditioningRead(`/api/document/site/${siteId}/parent/folders`);
      if (isCancelled()) return null;

      if (parentFoldersResponse?.parentFolders?.length > 0) {
        const logBooksFolder = parentFoldersResponse.parentFolders.find(
            (folder) => folder.name.trim() === "6 - Log Books"
        );

        if (logBooksFolder) {
          const logBooksResponse = await getAirConditioningRead(
              `/api/document/parent/${logBooksFolder.id}/folders?siteId=${siteId}`
          );
          if (isCancelled()) return null;

          if (logBooksResponse?.document?.childFolders) {
            const EnvironmentalLogBookFolder = logBooksResponse.document.childFolders.find(
                (folder) => folder.name.trim() === "Environmental Log Book"
            );

            if (EnvironmentalLogBookFolder) {
              const environmentalResponse = await getAirConditioningRead(
                  `/api/document/parent/${EnvironmentalLogBookFolder.id}/folders?siteId=${siteId}`
              );
              if (isCancelled()) return null;

              if (environmentalResponse?.document?.childFolders) {
                const airConditioningFolder = environmentalResponse.document.childFolders.find(
                    (folder) => folder.name === "Air Conditioning Service & Maintenance Records"
                );

                setFolderIds({
                  logBooks: logBooksFolder.id,
                  EnvironmentalLogBook: EnvironmentalLogBookFolder.id,
                  airConditioning: airConditioningFolder?.id || null,
                });

                return airConditioningFolder?.id || null;
              }
            }
          }
        }
      }
      return null;
    } catch {
      if (!isCancelled()) {
        toast.error("Failed to load document folders");
      }
      return null;
    }
  };

  useEffect(() => {
    // Avoid re-reading an action that the initial inspection load has already
    // resolved. Interactive action-id changes still fetch the authoritative row.
    const fetchActionData = async () => {
      if (
        formData.actionId &&
        String(existingAction?.actionId || "") !== String(formData.actionId)
      ) {
        const action = await fetchActionById(formData.actionId);
        if (action) {
          setExistingAction(action);
          setActionRaised(true);
        } else {
          setExistingAction(null);
          setActionRaised(false);
        }
      }
    };

    fetchActionData();
  }, [formData.actionId, existingAction?.actionId]);

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    const applySiteCheckState = () => {
      if (!siteCheck || Number(siteCheck.checkId) !== Number(checkId)) {
        return null;
      }

      setInspectionDetails(siteCheck);
      setCurrentCheckId(siteCheck.checkId);
      setCheckStatus(siteCheck.status);

      const isDone = siteCheck.status === "Done";
      setIsFormEditable(!isDone);
      setIsSubmitted(isDone);
      setShowPdfButton(isDone);
      return siteCheck;
    };

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const loadedSiteCheck = applySiteCheckState();
        if (!loadedSiteCheck || !authoritativeSiteId) {
          return;
        }

        let usersForInspection = Array.isArray(usersRef.current)
            ? usersRef.current
            : [];

        if (isInternalUserTaggedWithSite && usersForInspection.length === 0) {
          usersForInspection = await loadAirConditioningUsers(getUsers);
          if (isCancelled()) return;
        }

        const [assetResponse, loadedSiteDetails] = await Promise.all([
          getAirConditioningRead(
              `/api/site/${authoritativeSiteId}/assets?siteCheckSummary=true`
          ),
          getAirConditioningRead(`/api/site/site/${authoritativeSiteId}`),
        ]);
        if (isCancelled()) return;

        const loadedAssets = (assetResponse?.assets || []).filter(
            (asset) => Number(asset.siteId) === authoritativeSiteId
        );
        setSiteCheckAssets(loadedAssets);
        setSiteCheckSite(loadedSiteDetails || null);

        await fetchFolderStructure(authoritativeSiteId, isCancelled);
        if (isCancelled()) return;

        const inspectionLoad = await fetchInspectionData(
            loadedSiteCheck.status,
            loadedAssets,
            false,
            usersForInspection,
            isCancelled
        );
        if (isCancelled()) return;

        if (!inspectionLoad?.hasLinkedAction) {
          await fetchExistingActions(loadedSiteCheck.checkId, null, isCancelled);
          if (isCancelled()) return;
        }

        const siteData = loadedSiteDetails || authoritativeSiteRef.current;

        if (siteData) {
          const addressParts = [
            siteData.address1,
            siteData.address2,
            siteData.city,
            siteData.area,
            siteData.postCode,
            siteData.country,
          ].filter((part) => part && part.trim() !== "");

          const fullAddress = addressParts.join(", ");
          setFormData((prev) => ({ ...prev, address: fullAddress }));

          if (siteData.siteContact) {
            setFormData((prev) => ({
              ...prev,
              siteContact: siteData.siteContact.name || "",
              siteContactNo: siteData.siteContact.phone || "",
            }));
          }
        }
      } catch {
        if (!isCancelled()) {
          toast.error("Failed to load Site Check details and assets");
        }
      } finally {
        if (!isCancelled()) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [siteCheck, checkId, authoritativeSiteId, getUsers]);


  useEffect(() => {
    const shouldShowRiskAssessment = formData.param2 === "Pass"

    setShowRiskAssessment(shouldShowRiskAssessment);
    // Update actionRaised state based on existing action
    const isActionValid = existingAction && existingAction.checkId === currentCheckId;
    setActionRaised(isActionValid);
  }, [formData.param2, currentCheckId]);

  const handleRiskAssessmentComplete = async (actionResponse) => {
    try {
      if (!actionResponse?.actionId) {
        throw new Error("Invalid action response received");
      }

      // Verify the new action has our current checkId
      const verifiedAction = await fetchActionById(actionResponse.actionId);
      if (!verifiedAction || verifiedAction.checkId !== currentCheckId) {
        throw new Error("Action was not properly linked to this inspection");
      }

      setExistingAction(verifiedAction);
      setActionRaised(true);

      // Update form data
      setFormData(prev => ({
        ...prev,
        actionId: verifiedAction.actionId
      }));

      if (currentCheckId) {
        await post(
          `/api/site-check/generic-inspection`,
          buildInspectionPayload(currentCheckId, verifiedAction.actionId)
        );
        // Keep the values currently shown in the Open form after the
        // intermediate Risk Assessment save. Re-fetching must not reset a
        // date or engineer that the user has already selected.
        await fetchInspectionData(checkStatus, siteCheckAssets, true);
        toast.success(`Action #${verifiedAction.actionId} successfully linked to inspection`);
      }
    } catch (error) {
      toast.error(getSiteCheckErrorMessage(error, "Failed to process action completion"));

      // Rollback state changes if the operation failed
      setActionRaised(false);
      setExistingAction(null);
      setFormData(prev => ({ ...prev, actionId: null }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
      // The two signature dates on this report are intentionally not
      // independent dates. They follow the actual Inspection Date.
      ...(name === "inspectionDate" ? { signedDate: value } : {}),
    }));
  };

  const handleRefrigerantQuantityChange = (e) => {
    const rawValue = e.target.value.replace(",", ".");

    // Dan's requirement is a numeric value with at most one decimal place.
    // Empty is allowed because the existing Materials Used fields are optional.
    if (rawValue === "" || /^\d+(?:\.\d{0,1})?$/.test(rawValue)) {
      setFormData((prev) => ({
        ...prev,
        refrigerantQuantity: rawValue,
      }));
    }
  };

  const calculateExpiryDate = (visitDate, repeatFrequency) =>
    calculateSiteCheckDueDate(visitDate, repeatFrequency);

  const formatSiteCheckDueDate = (visitDate, repeatFrequency) => {
    const expiry = calculateExpiryDate(visitDate, repeatFrequency);
    return expiry ? `${expiry}T00:00:00` : null;
  };

  const buildInspectionPayload = (checkIdOverride, actionIdOverride, inspectionDateOverride, signedDateOverride) => ({
    address: formData.address,
    assetId: formData.selectedAsset?.assetId || formData.assetId,
    siteContact: formData.siteContactUser?.id || formData.siteContact,
    inspectionDate: toJavaLocalDate(inspectionDateOverride || formData.inspectionDate),
    siteContactNo: formData.siteContactNo,
    job: formData.job,
    manufacturer: formData.manufacturer || formData.selectedAsset?.manufacturer,
    modelNumber: formData.modelNumber || formData.selectedAsset?.model,
    position: formData.position || formData.selectedAsset?.position,
    floor: formData.floor || formData.selectedAsset?.floor,
    room: formData.room || formData.selectedAsset?.room,
    assetName: formData.assetName || formData.selectedAsset?.assetName,
    serialNumber: formData.serialNumber || formData.selectedAsset?.serialNumber,
    report: formData.report,
    param1: formData.param1,
    param2: formData.param2,
    param3: formData.param3,
    param4: formData.param4,
    param5: formData.param5,
    param6: formData.param6,
    param7: formData.param7,
    param8: formData.param8,
    param9: formData.param9,
    param10: formData.param10,
    param1Remark: formData.param1Remark,
    param2Remark: formData.param2Remark,
    // Legacy param3Remark is intentionally not reused for new AC submissions.
    // Existing historical rows remain untouched in the database.
    param3Remark: null,
    refrigerantType: formData.refrigerantType || null,
    refrigerantQuantity:
        formData.refrigerantQuantity === ""
            ? null
            : Number(formData.refrigerantQuantity),
    param4Remark: formData.param4Remark,
    param5Remark: formData.param5Remark,
    param6Remark: formData.param6Remark,
    client: formData.clientUser?.id || formData.client,
    engineer: formData.engineer,
    signedDate: toJavaLocalDate(signedDateOverride || formData.signedDate),
    actionId: actionIdOverride ?? formData.actionId,
    siteId: authoritativeSiteId,
    type: 'Inspection',
    subType: 'Air Conditioning',
    category: 'Air Conditioning Service',
    checkId: checkIdOverride ?? currentCheckId,
  });

  const savePdfToLocal = async (pdfBlob, fileName) => {
    try {
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
      return true;
    } catch {
      return false;
    }
  };

  const handleEngineerSelect = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      engineer: newValue?.id || "",
      user: newValue || {},
    }));

    setValidationErrors((prev) => ({
      ...prev,
      engineer: "",
    }));
  };

  const handleAssetSelect = (event, newValue) => {
    if (newValue && Number(newValue.siteId) !== authoritativeSiteId) {
      toast.error(wrongSiteAssetMessage);
      setFormData((prev) => ({ ...prev, assetId: "", selectedAsset: null }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      assetId: newValue ? newValue.assetId : "",
      selectedAsset: newValue || null,
    }));
  };
  const dateFormat = (date) => {
    return moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY');
  }



    const uploadPdfToServer = async (pdfBlob, fileName, inspectionDateOverride, checkIdOverride) => {
        try {
            setIsUploading(true);

            const inspectionDateForUpload = inspectionDateOverride || formData.inspectionDate;
            const resolvedCheckId = Number(checkIdOverride ?? currentCheckId);
            if (!Number.isInteger(resolvedCheckId) || resolvedCheckId <= 0) {
                throw new Error('Could not determine Site Check ID for PDF upload');
            }
            const sourceReference = `AC-${resolvedCheckId}-${Date.now()}`;

            const savedLocally = await savePdfToLocal(pdfBlob, fileName);
            if (!savedLocally) {
                throw new Error('Failed to save PDF locally');
            }

            const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
            const targetFolderId = folderIds.airConditioning || folderIds.logBooks;

            if (!targetFolderId) {
                throw new Error('Could not determine target folder for PDF upload');
            }

            const resolvedPdf = await resolveSiteCheckPdfTarget({
                fetchJson: getAirConditioningRead,
                siteId: authoritativeSiteId,
                checkId: resolvedCheckId,
                assetId: Number(selectedAsset?.assetId || formData.assetId),
                fileName,
                folderId: targetFolderId,
            });
            const uploadFormData = new FormData();

            if (resolvedPdf.exists) {
                uploadFormData.append('file', pdfFile);
                const documentRequestString = {
                    folderId: resolvedPdf.folderId,
                    files: [{
                        id: resolvedPdf.fileId,
                        name: fileName,
                        originalFileName: fileName,
                        fileVersion: resolvedPdf.fileVersion + 1,
                        assetId: Number(selectedAsset?.assetId || formData.assetId),
                        siteId: authoritativeSiteId || 0,
                        issueDate: toJavaLocalDateTime(inspectionDateForUpload),
                        expiryDate: toJavaLocalDateTime(calculateExpiryDate(inspectionDateForUpload, inspectionDetails?.repeatFrequency)),
                        uploaderUserId: loggedInUserData?.id || 0,
                        reviewerUserId: loggedInUserData?.id || 0,
                        referenceNumber: sourceReference
                    }]
                };

                uploadFormData.append('documentRequestString', JSON.stringify(documentRequestString));
                const response = await axios({
                    method: 'put',
                    url: '/api/document/file/newVersion/upload',
                    data: uploadFormData,
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Accept': 'application/json'
                    }
                });

                if (response?.status >= 200 && response?.status < 300) {
                    toast.success(`PDF uploaded successfully as version ${documentRequestString.fileVersion}!`);
                    return { stored: true, sourceReference };
                }
            } else {
                uploadFormData.append('files', pdfFile);
                const fileVersion = 1;

                const documentRequestString = {
                    folderId: targetFolderId,
                    files: [{
                        name: fileName.replace(/\.pdf$/i, ''),
                        issueDate: toJavaLocalDateTime(inspectionDateForUpload),
                        expiryDate: toJavaLocalDateTime(calculateExpiryDate(inspectionDateForUpload, inspectionDetails?.repeatFrequency)),
                        note: 'Air Conditioning Certificate',
                        fileVersion: fileVersion,
                        assetId: Number(selectedAsset?.assetId || formData.assetId),
                        siteId: authoritativeSiteId || 0,
                        originalFileName: fileName,
                        uploaderUserId: loggedInUserData?.id || 0,
                        reviewerUserId: loggedInUserData?.id || 0,
                        referenceNumber: sourceReference
                    }]
                };

                uploadFormData.append('documentRequestString', JSON.stringify(documentRequestString));
                const response = await axios({
                    method: 'post',
                    url: '/api/document/files/upload',
                    data: uploadFormData,
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response?.status >= 200 && response?.status < 300) {
                    toast.success(`PDF uploaded successfully as version ${fileVersion}!`);
                    return { stored: true, sourceReference };
                }
            }

            throw new Error('Upload failed: request did not return a successful status');
        } catch (uploadError) {
            console.error("Air Conditioning PDF upload:", uploadError);
            toast.error(uploadError?.response?.data?.detail || uploadError?.response?.data?.message || uploadError?.message || "PDF upload failed");
            return { stored: false, sourceReference: null };
        } finally {
            setIsUploading(false);
        }
    };

  const savePdfToPublic = async (pdfBlob, fileName) => {
    try {
      return true;
    } catch {
      return false;
    }
  };

  const generatePDF = async (uploadToServer = true, inspectionDateOverride, signedDateOverride, checkIdOverride) => {
    try {
      setIsGeneratingPDF(true);

      if (!PDFLib) {
        PDFLib = await import('pdf-lib');
      }

      const pdfBytes = await fetchPdfTemplate();
      const pdfDoc = await PDFLib.PDFDocument.load(pdfBytes);
      const form = pdfDoc.getForm();

      const setTextField = (fieldName, value, fontSize = 10) => {
        try {
          const field = form.getTextField(fieldName);
          if (field) {
            field.setText(value || '');
            try {
              if (field.setFontSize) {
                field.setFontSize(fontSize);
              }
            } catch {
              // Font size not supported for this field
            }
          }
        } catch {
          // Field missing or incompatible in template
        }
      };

      const setCheckbox = (fieldName, isChecked) => {
        try {
          const field = form.getCheckBox(fieldName);
          if (field) {
            field.check(isChecked);
          }
        } catch {
          // Field missing or incompatible in template
        }
      };

      const smallFont = 8;
      const mediumFont = 10;
      const effectiveInspectionDate = inspectionDateOverride || formData.inspectionDate;

      // Address and contact information
      const addressLines = (formData.address || '').split(',');
      setTextField('Address', addressLines[0] || '', mediumFont);
      setTextField('Address_2', addressLines[1] || '', mediumFont);
      setTextField('Address_3', addressLines[2] || '', mediumFont);
      setTextField('Address_4', addressLines[3] || '', mediumFont);
      setTextField('Address_4', addressLines[4] || '', mediumFont);


      setTextField('Date', dateFormat(effectiveInspectionDate), mediumFont);
      setTextField('Site Contact', formData.siteContactUser?.name || formData.siteContact || '', mediumFont);
      setTextField('Site Contact No', formData.siteContactNo || '', mediumFont);
      setTextField('Job No', formData.job || '', mediumFont);


      const equipmentDetailsLocation = [
        selectedAsset?.floor,
        selectedAsset?.room,
        selectedAsset?.position,
        selectedAsset?.assetName
      ].filter(Boolean).join(' - ');

      // Equipment information
      setTextField('Manufacturer', selectedAsset?.manufacturer || '', mediumFont);
      setTextField('Model Number', selectedAsset?.model || '', mediumFont);
      setTextField('Serial Number', selectedAsset?.serialNumber || '', mediumFont);
      setTextField('Equipment Details  Location', equipmentDetailsLocation || '', mediumFont);


      const mapPassFailToYesNo = (value) => {
        if (value === "Pass") return "Yes";
        if (value === "Fail") return "No";
        return ""; // or whatever default you want for empty values
      };
      // Service checkboxes
      setTextField('Job Complete', mapPassFailToYesNo(formData.param1) || '', mediumFont);
      setTextField('Parts Required', mapPassFailToYesNo(formData.param2) || '', mediumFont);
      setTextField('F Gas Check Complete', mapPassFailToYesNo(formData.param3) || '', mediumFont);
      setTextField('Filters Cleaned', mapPassFailToYesNo(formData.param4) || '', mediumFont);
      setTextField('Indoor Coil Cleaned', mapPassFailToYesNo(formData.param5) || '', mediumFont);
      setTextField('Outdoor Coil Cleaned', mapPassFailToYesNo(formData.param6) || '', mediumFont);
      setTextField('System Leak Check', mapPassFailToYesNo(formData.param7) || '', mediumFont);
      setTextField('Drain  Pump Test', mapPassFailToYesNo(formData.param8) || '', mediumFont);
      setTextField('Electrical Connections Check', mapPassFailToYesNo(formData.param9) || '', mediumFont);
      setTextField('Temperature Checks', mapPassFailToYesNo(formData.param10) || '', mediumFont);



      // Materials used
      setTextField('OFN', formData.param1Remark || '', mediumFont);
      setTextField('Welding', formData.param2Remark || '', mediumFont);
      setTextField('Refrigerant Type', formData.refrigerantType || '', mediumFont);
      setTextField(
          'Refrigerant Quantity',
          formData.refrigerantQuantity === null || formData.refrigerantQuantity === undefined
              ? ''
              : String(formData.refrigerantQuantity),
          mediumFont
      );
      setTextField('Reclaim Cylinder', formData.param4Remark || '', mediumFont);
      setTextField('Cleaning Chemicals', formData.param5Remark || '', mediumFont);
      setTextField('Air Spray', formData.param6Remark || '', mediumFont);

      // Report
      setTextField('Engineers Report', formData.report || '', mediumFont);

      // Signatures
      const clientName = formData.clientUser?.name || formData.client || '';
      const engineerName = selectedEngineer?.name || formData.user?.name || '';

      setTextField('Clients Name', clientName, mediumFont);
      setTextField('Engineers Name', engineerName, mediumFont);
      setTextField('on', dateFormat(signedDateOverride || formData.signedDate), smallFont);
      setTextField('on_2', dateFormat(signedDateOverride || formData.signedDate), smallFont);

      // Flatten and save
      form.flatten();
      const pdfBytesModified = await pdfDoc.save();
      const blob = new Blob([pdfBytesModified], { type: 'application/pdf' });
      const fileName = `AirConditioningReport_${selectedAsset?.assetName || "report"}.pdf`;

      setGeneratedPdfBlob(blob);
      setShowPdfButton(true);

      let uploadResult = { stored: false, sourceReference: null };
      if (uploadToServer) {
        uploadResult = await uploadPdfToServer(
          blob,
          fileName,
          effectiveInspectionDate,
          checkIdOverride
        );
      }

      toast.success('PDF generated successfully!');
      return { success: true, fileName, uploadResult };

    } catch (error) {
      toast.error('Failed to generate PDF');
      return { success: false, error: error.message };
    } finally {
      setIsGeneratingPDF(false);
    }
  };
  const waitForHistoryRetry = (milliseconds) =>
    new Promise((resolve) => setTimeout(resolve, milliseconds));

  const recordAirConditioningHistory = async ({
    checkId: historyCheckId,
    inspectionRecordId,
    sourceReference,
  }) => {
    const parsedCheckId = Number(historyCheckId);
    const parsedInspectionRecordId = Number(inspectionRecordId);
    const normalisedSourceReference = String(sourceReference || "").trim();

    if (!Number.isInteger(parsedCheckId) || parsedCheckId <= 0) {
      throw new Error("A valid Site Check ID is required to record History.");
    }
    if (!Number.isInteger(parsedInspectionRecordId) || parsedInspectionRecordId <= 0) {
      throw new Error("A valid inspection record ID is required to record History.");
    }
    if (!normalisedSourceReference) {
      throw new Error("A PDF source reference is required to record History.");
    }

    let lastError;

    for (let attempt = 1; attempt <= 2; attempt += 1) {
      try {
        const response = await post(
          `/api/site-check/${parsedCheckId}/history/air-conditioning`,
          {
            sourceReference: normalisedSourceReference,
            inspectionRecordId: parsedInspectionRecordId,
          }
        );

        if (![200, 201, 204].includes(response?.status)) {
          throw new Error(
            `History API returned unexpected status ${response?.status ?? "unknown"}.`
          );
        }

        const history = response?.data;
        if (!history?.historyId) {
          throw new Error("History API returned success without a History record ID.");
        }
        if (Number(history.checkId) !== parsedCheckId) {
          throw new Error("History API returned a record for a different Site Check.");
        }
        if (history.sourceReference !== normalisedSourceReference) {
          throw new Error("History API returned a different PDF source reference.");
        }

        return history;
      } catch (error) {
        lastError = error;
        const status = error?.response?.status;

        // Authentication/authorization failures must not be retried. All other
        // failures get one idempotent retry using the exact same identifiers.
        if (attempt >= 2 || status === 401 || status === 403) {
          break;
        }

        await waitForHistoryRetry(500);
      }
    }

    throw lastError || new Error("History record could not be created.");
  };

  const retryPendingAirConditioningHistory = async () => {
    if (!pendingHistoryRetry || isRetryingHistory) {
      return;
    }

    setIsRetryingHistory(true);
    try {
      const history = await recordAirConditioningHistory(pendingHistoryRetry);
      setPendingHistoryRetry(null);
      toast.success(`History recorded successfully (History #${history.historyId}).`);
    } catch (error) {
      const message = getSiteCheckErrorMessage(
        error,
        "History record could not be created."
      );
      console.error("Retry Air Conditioning history:", {
        ...pendingHistoryRetry,
        status: error?.response?.status,
        message,
        error,
      });
      toast.error(`History retry failed: ${message}`);
    } finally {
      setIsRetryingHistory(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoading) {
      return;
    }

    // Validation checks
    const hasFailures = [
      formData.param2
    ].some(val => val === "Pass" || val === "Yes");

    if (hasFailures && !actionRaised) {
      toast.error("Please complete the risk assessment before submitting");
      return;
    }
    if (formData.param2 === "Pass" && !actionRaised) {
      toast.error("Parts are required - please complete the risk assessment");
      return;
    }

    if (!isFormEditable) {
      return;
    }

    // Form validation
    const errors = {};
    const assetForSubmission = siteCheckAssets.find(
        (asset) => Number(asset.assetId) === Number(formData.assetId)
    );

    if (!assetForSubmission) {
      errors.assetId = formData.assetId
          ? wrongSiteAssetMessage
          : "Please select an Air Conditioning Unit from the Site Check site.";
    } else if (Number(assetForSubmission.siteId) !== authoritativeSiteId) {
      errors.assetId = wrongSiteAssetMessage;
    }

    if (!formData.engineer || !selectedEngineer) {
      errors.engineer = "Please select an active engineer for this Site Check.";
    }

    if (!formData.param1) errors.param1 = "Please select one option";
    if (!formData.param2) errors.param2 = "Please select one option";
    if (!formData.param3) errors.param3 = "Please select one option";
    if (!formData.param4) errors.param4 = "Please select one option";
    if (!formData.param5) errors.param5 = "Please select one option";
    if (!formData.param6) errors.param6 = "Please select one option";
    if (!formData.param7) errors.param7 = "Please select one option";
    if (!formData.param8) errors.param8 = "Please select one option";
    if (!formData.param9) errors.param9 = "Please select one option";
    if (!formData.param10) errors.param10 = "Please select one option";

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      if (errors.assetId) {
        toast.error(errors.assetId);
      } else if (errors.engineer) {
        toast.error(errors.engineer);
      }
      return;
    }

    setValidationErrors({});
    setIsLoading(true);

    try {
      // The Open form may default to today's UK date, but once the user edits
      // the control its value is authoritative for API/PDF persistence.
      const submissionInspectionDate = formData.inspectionDate;
      // Signed Date is read-only and follows the Inspection Date.
      const submissionSignedDate = submissionInspectionDate;

      const statusPayload = {
        siteId: parseInt(authoritativeSiteId, 10),
        type: 'Inspection',
        subType: 'Plant and Equipment Inspection',
        category: 'Air Conditioning Service',
        status: 'Done',
        // The actual Inspection Date is the authoritative start of the next cycle.
        startDate: toJavaLocalDateTime(submissionInspectionDate),
        dueDate: formatSiteCheckDueDate(submissionInspectionDate, inspectionDetails?.repeatFrequency),
        leadUserID: loggedInUserData?.id ? String(loggedInUserData.id) : '0',
        assistantUserID: loggedInUserData?.id ? String(loggedInUserData.id) : '0'
      };

      let statusResponse;
      let checkIdForSave = currentCheckId;
      if (currentCheckId) {
        statusPayload.checkId = parseInt(currentCheckId, 10);
        // Existing checks use the completion endpoint so only the completion
        // status/start/due fields are changed. Assignees and schedule settings
        // remain untouched.
        statusResponse = await put(
            `/api/site-check/${currentCheckId}/completion`,
            statusPayload
        );
      } else {
        statusResponse = await post(
            `/api/site-check`,
            statusPayload
        );
        checkIdForSave = statusResponse?.data?.checkId;
        if (checkIdForSave) {
          setCurrentCheckId(checkIdForSave);
        }
      }

      if (![200, 201, 204].includes(statusResponse?.status)) {
        throw new Error('Failed to update site check status');
      }

      setCheckStatus('Done');
      setIsFormEditable(false);

      const resolvedCheckIdForSave =
        checkIdForSave || statusResponse?.data?.checkId;

      const saveResponse = await post(
        `/api/site-check/generic-inspection`,
        buildInspectionPayload(
            resolvedCheckIdForSave,
            undefined,
            submissionInspectionDate,
            submissionSignedDate
        )
      );

      if (![200, 201, 204].includes(saveResponse?.status)) {
        throw new Error('Failed to save inspection data');
      }

      const savedInspectionRecordId = saveResponse?.data?.id;

      // Keep the existing PDF flow. The returned upload metadata is used only
      // to link this exact Air Conditioning submission to immutable history.
      const pdfResult = await generatePDF(
        true,
        submissionInspectionDate,
        submissionSignedDate,
        resolvedCheckIdForSave
      );
      if (!pdfResult.success) {
        throw new Error(pdfResult.error || "Failed to generate PDF");
      }

      const parsedCheckId = Number(resolvedCheckIdForSave);
      const parsedInspectionRecordId = Number(savedInspectionRecordId);
      const sourceReference = String(
        pdfResult?.uploadResult?.sourceReference || ""
      ).trim();

      if (!Number.isInteger(parsedCheckId) || parsedCheckId <= 0) {
        throw new Error(
          "Air Conditioning was saved, but History cannot be recorded because the Site Check ID is invalid."
        );
      }
      if (!Number.isInteger(parsedInspectionRecordId) || parsedInspectionRecordId <= 0) {
        throw new Error(
          "Air Conditioning was saved, but History cannot be recorded because the saved inspection record ID was not returned."
        );
      }
      if (!pdfResult?.uploadResult?.stored) {
        throw new Error(
          "Air Conditioning was saved, but History cannot be recorded because the PDF was not stored in Site Documents."
        );
      }
      if (!sourceReference) {
        throw new Error(
          "Air Conditioning was saved, but History cannot be recorded because the PDF reference was not returned."
        );
      }

      const historyPayload = {
        checkId: parsedCheckId,
        inspectionRecordId: parsedInspectionRecordId,
        sourceReference,
      };

      try {
        await recordAirConditioningHistory(historyPayload);
        setPendingHistoryRetry(null);
      } catch (historyError) {
        const historyMessage = getSiteCheckErrorMessage(
          historyError,
          "History record could not be created."
        );

        console.error("Record Air Conditioning history:", {
          checkId: parsedCheckId,
          inspectionRecordId: parsedInspectionRecordId,
          sourceReference,
          status: historyError?.response?.status,
          message: historyMessage,
          error: historyError,
        });

        // The existing inspection save and PDF upload have already succeeded.
        // Do not resubmit the inspection or create another PDF version. Keep the
        // exact identifiers so History alone can be retried safely/idempotently.
        setPendingHistoryRetry(historyPayload);
        setShowPdfButton(true);
        setIsSubmitted(true);
        setSubmissionSuccess(true);
        toast.error(
          `Report and PDF were saved, but History was not recorded: ${historyMessage}`
        );
        return;
      }

      toast.success("Air Conditioning report saved, PDF generated, and History recorded successfully!");
      setShowPdfButton(true);
      setIsSubmitted(true);
      setSubmissionSuccess(true);

      setTimeout(() => {
        if (!embedded) {
          navigate(-1);
        }
      }, 1500);

    } catch (error) {
      const apiMessage = error.response?.data?.message || error.response?.data;
      toast.error(
        apiMessage
          ? (typeof apiMessage === 'string' ? apiMessage : JSON.stringify(apiMessage))
          : (error.message || 'Failed to submit form')
      );
    } finally {
      setIsLoading(false);
    }
  };
  const renderClientNameField = () => {
    if (isInternalUserTaggedWithSite) {
      const filteredUsers =
          users?.filter((user) =>
              user.taggedSites?.some(
                  (site) => Number(site.id ?? site.siteId) === authoritativeSiteId
              )
          ) || [];

      return (
          <Autocomplete
              options={filteredUsers}
              getOptionLabel={(user) => user.name}
              value={formData.clientUser || formData.siteContactUser || null}
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  client: newValue?.id || "",
                  clientUser: newValue || null,
                  siteContact: newValue?.id || "",
                  siteContactNo: newValue?.phone || "",
                  siteContactUser: newValue || null,
                }));
              }}
              renderInput={(params) => (
                  <TextField
                      {...params}
                      variant="outlined"
                      required
                      disabled={isSubmitted}
                      style={{
                        height: "40px",
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                        },
                        "& .MuiAutocomplete-input": {
                          padding: "8.5px 4px !important",
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          padding: "0 5px",
                        },
                      }}
                  />
              )}
              disabled={isSubmitted}
          />
      );
    }
    return (
        <input
            type="text"
            className="form-control"
            name="clientName"
            value={
                formData.clientUser?.name || formData.siteContactUser?.name || ""
            }
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                client: e.target.value,
                clientNameText: e.target.value,
                siteContact: e.target.value,
                siteContactName: e.target.value,
              }));
            }}
            required
            disabled={isSubmitted}
        />
    );
  };

  const renderSiteContactField = () => {
    if (isInternalUserTaggedWithSite) {
      const filteredUsers =
          users?.filter((user) =>
              user.taggedSites?.some(
                  (site) => Number(site.id ?? site.siteId) === authoritativeSiteId
              )
          ) || [];

      return (
          <Autocomplete
              options={filteredUsers}
              getOptionLabel={(user) => user.name}
              value={formData.siteContactUser || formData.clientUser || null}
              onChange={(event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  siteContact: newValue?.id || "",
                  siteContactNo: newValue?.phone || "",
                  siteContactUser: newValue || null,
                  client: newValue?.id || "",
                  clientUser: newValue || null,
                }));
              }}
              renderInput={(params) => (
                  <TextField
                      {...params}
                      variant="outlined"
                      required
                      disabled={isSubmitted}
                      style={{
                        height: "40px",
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                        },
                        "& .MuiAutocomplete-input": {
                          padding: "8.5px 4px !important",
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          padding: "0 5px",
                        },
                      }}
                  />
              )}
              disabled={isSubmitted}
          />
      );
    }
    return (
        <input
            type="text"
            className="form-control"
            name="siteContact"
            value={
                formData.siteContactUser?.name || formData.clientUser?.name || ""
            }
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                siteContact: e.target.value,
                siteContactName: e.target.value,
                client: e.target.value,
                clientNameText: e.target.value,
              }));
            }}
            required
            disabled={isSubmitted}
        />
    );
  };

  const filteredAssets =
      siteCheckAssets?.filter(
          (asset) =>
              Number(asset.siteId) === authoritativeSiteId &&
              asset.category === "Mechanical" &&
              asset.subCategory === "Air Conditioning" &&
              (asset.subCategory2 === "Air Conditioning Unit (Indoor)" || asset.subCategory2 === "Air Conditioning Unit (Outdoor)")
      ) || [];

  const repeatFrequency = inspectionDetails?.repeatFrequency || siteCheck?.repeatFrequency || "None";
  const nextInspectionDate =
      repeatFrequency !== "None"
        ? (calculateExpiryDate(formData.inspectionDate, repeatFrequency) || "")
        : "";


  return (
      <div className="container mt-4 mb-5">
        <div className="header text-center bg-light p-4 mb-4 rounded d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Air Conditioning Service Report</h4>
        </div>
        {!isFormEditable && (
            <div className="alert alert-warning" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              This form is read-only because the check has been marked as completed.
            </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="mb-3 d-flex">
                <label
                    className="form-label"
                    style={{ fontWeight: "bold", marginRight: "20px" }}
                >
                  Address
                </label>
                <textarea
                    className="form-control"
                    rows={3}
                    name="address"
                    value={formData.address || ""}
                    disabled
                    style={{
                      width: "300px",
                      height: "150px",
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                      backgroundColor: "#f8f9fa",
                      fontWeight: "normal",
                      fontSize: "15px",
                    }}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="mb-3">
                <label className="form-label">Inspection Date</label>
                <input
                    type="date"
                    className="form-control"
                    name="inspectionDate"
                    value={formatDate(formData.inspectionDate)}
                    onChange={handleInputChange}
                    required
                    style={{
                      height: "40px",
                      padding: "0 10px",
                      width: "100%",
                    }}
                    disabled={isSubmitted}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Site Contact</label>
                {renderSiteContactField()}
              </div>
            </div>
            <div className="col-md-3">
              <div className="mb-3">
                <label className="form-label">Site Contact No.</label>
                <input
                    type="text"
                    className="form-control"
                    name="siteContactNo"
                    value={formData.siteContactNo}
                    onChange={handleInputChange}
                    disabled={isSubmitted}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Job No.</label>
                <input
                    type="text"
                    className="form-control"
                    name="job"
                    value={formData.job}
                    onChange={handleInputChange}
                    disabled={isSubmitted}
                />
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Device Information</h5>
            </div>
            <div className="card-body">
              <div className="row mb-4">
                <div className="col-md-12">
                  <Autocomplete
                      disabled={isSubmitted}
                      options={filteredAssets}
                      getOptionLabel={(option) =>
                          `${option.assetId} - ${option.assetName} (${
                              option.position || "NA"
                          } > ${option.floor || "NA"} > ${option.room || "NA"})`
                      }
                      value={selectedAsset}
                      onChange={handleAssetSelect}
                      renderInput={(params) => (
                          <TextField
                              {...params}
                              label="Select an Air Conditioning Unit"
                              variant="outlined"
                              placeholder="Search devices..."
                              error={Boolean(validationErrors.assetId)}
                              helperText={validationErrors.assetId || ""}
                          />
                      )}
                      sx={{ width: "100%" }}
                  />
                </div>
              </div>

              {formData.selectedAsset && (
                  <div className="row">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Manufacturer</label>
                        <input
                            type="text"
                            className="form-control"
                            name="manufacturer"
                            value={selectedAsset?.manufacturer}
                            onChange={handleInputChange}
                            required
                            disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Model Number</label>
                        <input
                            type="text"
                            className="form-control"
                            name="modelNumber"
                            value={selectedAsset?.model}
                            onChange={handleInputChange}
                            required
                            disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Serial Number</label>
                        <input
                            type="text"
                            className="form-control"
                            name="serialNumber"
                            value={selectedAsset?.serialNumber}
                            onChange={handleInputChange}
                            required
                            disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Asset No</label>
                        <input
                            type="text"
                            className="form-control"
                            name="assetId"
                            value={`Asset No - ${formData.selectedAsset?.assetId}`}
                            onChange={handleInputChange}
                            required
                            disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Position</label>
                        <input
                            type="text"
                            className="form-control"
                            name="position"
                            value={selectedAsset?.position}
                            onChange={handleInputChange}
                            required
                            disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Floor</label>
                        <input
                            type="text"
                            className="form-control"
                            name="floor"
                            value={selectedAsset?.floor}
                            onChange={handleInputChange}
                            required
                            disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Room</label>
                        <input
                            type="text"
                            className="form-control"
                            name="room"
                            value={selectedAsset?.room}
                            onChange={handleInputChange}
                            required
                            disabled
                        />
                      </div>
                    </div>
                  </div>
              )}
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Engineers Report</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <TextField
                    multiline
                    rows={16}
                    fullWidth
                    variant="outlined"
                    value={formData.report || ""}
                    onChange={(e) =>
                        setFormData({
                          ...formData,
                          report: e.target.value,
                        })
                    }
                    style={{ height: "400px" }}
                    disabled={isSubmitted}
                />
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered">
                  <tbody>
                  <tr>
                    <td style={{ textAlign: "center", fontWeight: "bold" }}>
                      Job Complete
                    </td>
                    <td style={{ textAlign: "center", fontWeight: "bold" }}>
                      Parts Required
                    </td>
                    <td style={{ textAlign: "center", fontWeight: "bold" }}>
                      F Gas Check Complete
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <select
                          className={`form-select ${
                              validationErrors.param1 ? "is-invalid" : ""
                          }`}
                          value={formData.param1}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              param1: e.target.value,
                            });
                            if (validationErrors.param1) {
                              setValidationErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.param1;
                                return newErrors;
                              });
                            }
                          }}
                          disabled={isSubmitted}
                      >
                        <option value="">Select</option>
                        <option value="Pass">Yes</option>
                        <option value="Fail">No</option>
                      </select>
                      {validationErrors.param1 && (
                          <div className="invalid-feedback">
                            {validationErrors.param1}
                          </div>
                      )}
                    </td>
                    <td>
                      <select
                          className={`form-select ${
                              validationErrors.param2 ? "is-invalid" : ""
                          }`}
                          value={formData.param2}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              param2: e.target.value,
                            });
                            if (validationErrors.param2) {
                              setValidationErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.param2;
                                return newErrors;
                              });
                            }
                          }}
                          disabled={isSubmitted}
                      >
                        <option value="">Select</option>
                        <option value="Pass">Yes</option>
                        <option value="Fail">No</option>
                      </select>
                      {validationErrors.param2 && (
                          <div className="invalid-feedback">
                            {validationErrors.param2}
                          </div>
                      )}
                    </td>
                    <td>
                      <select
                          className={`form-select ${
                              validationErrors.param3 ? "is-invalid" : ""
                          }`}
                          value={formData.param3}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              param3: e.target.value,
                            });
                            if (validationErrors.param3) {
                              setValidationErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.param3;
                                return newErrors;
                              });
                            }
                          }}
                          disabled={isSubmitted}
                      >
                        <option value="">Select</option>
                        <option value="Pass">Yes</option>
                        <option value="Fail">No</option>
                      </select>
                      {validationErrors.param3 && (
                          <div className="invalid-feedback">
                            {validationErrors.param3}
                          </div>
                      )}
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="mb-4 card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h5>Service Items Undertaken</h5>
                  <div className="d-flex flex-column gap-3 mt-3">
                    <div>
                      <label className="form-label fw-bold">
                        Filters Cleaned
                      </label>
                      <select
                          className={`form-select ${
                              validationErrors.param4 ? "is-invalid" : ""
                          }`}
                          value={formData.param4}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              param4: e.target.value,
                            });
                            if (validationErrors.param4) {
                              setValidationErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.param4;
                                return newErrors;
                              });
                            }
                          }}
                          disabled={isSubmitted}
                      >
                        <option value="">Select</option>
                        <option value="Pass">Yes</option>
                        <option value="Fail">No</option>
                      </select>
                      {validationErrors.param4 && (
                          <div className="invalid-feedback">
                            {validationErrors.param4}
                          </div>
                      )}
                    </div>

                    <div>
                      <label className="form-label fw-bold">
                        Indoor Coil Cleaned
                      </label>
                      <select
                          className={`form-select ${
                              validationErrors.param5 ? "is-invalid" : ""
                          }`}
                          value={formData.param5}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              param5: e.target.value,
                            });
                            if (validationErrors.param5) {
                              setValidationErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.param5;
                                return newErrors;
                              });
                            }
                          }}
                          disabled={isSubmitted}
                      >
                        <option value="">Select</option>
                        <option value="Pass">Yes</option>
                        <option value="Fail">No</option>
                      </select>
                      {validationErrors.param5 && (
                          <div className="invalid-feedback">
                            {validationErrors.param5}
                          </div>
                      )}
                    </div>

                    <div>
                      <label className="form-label fw-bold">
                        Outdoor Coil Cleaned
                      </label>
                      <select
                          className={`form-select ${
                              validationErrors.param6 ? "is-invalid" : ""
                          }`}
                          value={formData.param6}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              param6: e.target.value,
                            });
                            if (validationErrors.param6) {
                              setValidationErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.param6;
                                return newErrors;
                              });
                            }
                          }}
                          disabled={isSubmitted}
                      >
                        <option value="">Select</option>
                        <option value="Pass">Yes</option>
                        <option value="Fail">No</option>
                      </select>
                      {validationErrors.param6 && (
                          <div className="invalid-feedback">
                            {validationErrors.param6}
                          </div>
                      )}
                    </div>

                    <div>
                      <label className="form-label fw-bold">
                        System Leak Check
                      </label>
                      <select
                          className={`form-select ${
                              validationErrors.param7 ? "is-invalid" : ""
                          }`}
                          value={formData.param7}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              param7: e.target.value,
                            });
                            if (validationErrors.param7) {
                              setValidationErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.param7;
                                return newErrors;
                              });
                            }
                          }}
                          disabled={isSubmitted}
                      >
                        <option value="">Select</option>
                        <option value="Pass">Yes</option>
                        <option value="Fail">No</option>
                      </select>
                      {validationErrors.param7 && (
                          <div className="invalid-feedback">
                            {validationErrors.param7}
                          </div>
                      )}
                    </div>

                    <div>
                      <label className="form-label fw-bold">
                        Drain/Pump Test
                      </label>
                      <select
                          className={`form-select ${
                              validationErrors.param8 ? "is-invalid" : ""
                          }`}
                          value={formData.param8}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              param8: e.target.value,
                            });
                            if (validationErrors.param8) {
                              setValidationErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.param8;
                                return newErrors;
                              });
                            }
                          }}
                          disabled={isSubmitted}
                      >
                        <option value="">Select</option>
                        <option value="Pass">Yes</option>
                        <option value="Fail">No</option>
                      </select>
                      {validationErrors.param8 && (
                          <div className="invalid-feedback">
                            {validationErrors.param8}
                          </div>
                      )}
                    </div>

                    <div>
                      <label className="form-label fw-bold">
                        Electrical Connections Check
                      </label>
                      <select
                          className={`form-select ${
                              validationErrors.param9 ? "is-invalid" : ""
                          }`}
                          value={formData.param9}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              param9: e.target.value,
                            });
                            if (validationErrors.param9) {
                              setValidationErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.param9;
                                return newErrors;
                              });
                            }
                          }}
                          disabled={isSubmitted}
                      >
                        <option value="">Select</option>
                        <option value="Pass">Yes</option>
                        <option value="Fail">No</option>
                      </select>
                      {validationErrors.param9 && (
                          <div className="invalid-feedback">
                            {validationErrors.param9}
                          </div>
                      )}
                    </div>

                    <div>
                      <label className="form-label fw-bold">
                        Temperature Checks
                      </label>
                      <select
                          className={`form-select ${
                              validationErrors.param10 ? "is-invalid" : ""
                          }`}
                          value={formData.param10}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              param10: e.target.value,
                            });
                            if (validationErrors.param10) {
                              setValidationErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.param10;
                                return newErrors;
                              });
                            }
                          }}
                          disabled={isSubmitted}
                      >
                        <option value="">Select</option>
                        <option value="Pass">Yes</option>
                        <option value="Fail">No</option>
                      </select>
                      {validationErrors.param10 && (
                          <div className="invalid-feedback">
                            {validationErrors.param10}
                          </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <h5>Materials Used</h5>
                  <div className="d-flex flex-column gap-3 mt-3">
                    <div>
                      <label className="form-label fw-bold">OFN</label>
                      <input
                          type="text"
                          className="form-control"
                          value={formData.param1Remark}
                          onChange={(e) =>
                              setFormData({ ...formData, param1Remark: e.target.value })
                          }
                          disabled={isSubmitted}
                      />
                    </div>

                    <div>
                      <label className="form-label fw-bold">Welding</label>
                      <input
                          type="text"
                          className="form-control"
                          value={formData.param2Remark}
                          onChange={(e) =>
                              setFormData({ ...formData, param2Remark: e.target.value })
                          }
                          disabled={isSubmitted}
                      />
                    </div>

                    <div>
                      <label className="form-label fw-bold">Refrigerant Type</label>
                      <select
                          className="form-select"
                          value={formData.refrigerantType}
                          onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                refrigerantType: e.target.value,
                              }))
                          }
                          disabled={isSubmitted}
                      >
                        <option value="">Select</option>
                        <option value="R410A">R410A</option>
                        <option value="R32">R32</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label fw-bold">Refrigerant Quantity</label>
                      <input
                          type="text"
                          className="form-control"
                          value={formData.refrigerantQuantity}
                          onChange={handleRefrigerantQuantityChange}
                          disabled={isSubmitted}
                          inputMode="decimal"
                          title="Enter a non-negative number with at most one decimal place"
                      />
                      {!formData.refrigerantType &&
                          !formData.refrigerantQuantity &&
                          formData.param3Remark && (
                              <div className="form-text">
                                Legacy saved refrigerant value: {formData.param3Remark}
                              </div>
                          )}
                    </div>
                    <div>
                      <label className="form-label fw-bold">
                        Reclaim Cylinder
                      </label>
                      <input
                          type="text"
                          className="form-control"
                          value={formData.param4Remark}
                          onChange={(e) =>
                              setFormData({
                                ...formData,
                                param4Remark: e.target.value,
                              })
                          }
                          disabled={isSubmitted}
                      />
                    </div>
                    <div>
                      <label className="form-label fw-bold">
                        Cleaning Chemicals
                      </label>
                      <input
                          type="text"
                          className="form-control"
                          value={formData.param5Remark}
                          onChange={(e) =>
                              setFormData({
                                ...formData,
                                param5Remark: e.target.value,
                              })
                          }
                          disabled={isSubmitted}
                      />
                    </div>
                    <div>
                      <label className="form-label fw-bold">Air Spray</label>
                      <input
                          type="text"
                          className="form-control"
                          value={formData.param6Remark}
                          onChange={(e) =>
                              setFormData({ ...formData, param6Remark: e.target.value })
                          }
                          disabled={isSubmitted}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {showRiskAssessment && (
                <div className="card mb-4">
                  <div className="card-header">
                    <h5 className="mb-0">Risk Assessment</h5>
                    {existingAction?.checkId === currentCheckId && (
                        <span className="badge bg-success ms-2">
          Action #{existingAction.actionId} - {existingAction.status}
        </span>
                    )}
                  </div>
                  <div className="card-body">
                    {existingAction?.checkId === currentCheckId ? (
                        <div className="existing-action">
                          <div className="row">
                            <div className="col-md-6">
                              <p><strong>Observation:</strong> {existingAction.observation}</p>
                              <p><strong>Required Action:</strong> {existingAction.requiredAction}</p>
                              <p><strong>Risk Score:</strong> {existingAction.riskScore}</p>
                            </div>
                            <div className="col-md-6">
                              <p><strong>Description: </strong> {existingAction.desc}</p>
                              <p><strong>Due Date:</strong> {formatDate(existingAction.dueDate)}</p>
                              <p><strong>Status:</strong> {existingAction.status}</p>
                            </div>
                          </div>
                          {existingAction.comments && (
                              <div className="mt-3">
                                <h6>Comments:</h6>
                                <p>{existingAction.comments}</p>
                              </div>
                          )}
                        </div>
                    ) : (
                        <RiskScoreCard
                            desc={`Inspection - Plant and Equipment Inspection - Air Conditioning Service`}
                            siteId={authoritativeSiteId}
                            checkId={currentCheckId}
                            createdBy={loggedInUserData?.id}
                            taggedAsset={selectedAsset?.assetId}
                            onRiskAssessmentComplete={handleRiskAssessmentComplete}
                            actionRaised={actionRaised}
                            disabled={isSubmitted}
                        />
                    )}
                  </div>
                </div>
            )}
          </div>

          <div className="row mt-4">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-bold">Client's Name</label>
                {renderClientNameField()}
              </div>

              <div className="mb-3">
                <label className="form-label">Signed Date</label>
                <input
                    type="date"
                    className="form-control"
                    name="signedDate"
                    value={formatDate(formData.signedDate)}
                    readOnly
                    required
                    style={{
                      height: "40px",
                      padding: "0 10px",
                      width: "100%",
                      backgroundColor: "#f8f9fa",
                    }}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-bold">Engineer's Name</label>
                <Autocomplete
                    options={engineerOptions}
                    value={selectedEngineer}
                    getOptionLabel={getUserLabel}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value?.id)
                    }
                    onChange={handleEngineerSelect}
                    disableClearable={checkStatus === "Open"}
                    noOptionsText="No active users are assigned to this site"
                    disabled={isSubmitted || !isFormEditable}
                    renderInput={(params) => (
                      <TextField
                          {...params}
                          required
                          variant="outlined"
                          placeholder="Select engineer"
                          error={Boolean(validationErrors.engineer)}
                          helperText={validationErrors.engineer || ""}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "40px",
                              padding: "0 8px",
                            },
                          }}
                      />
                    )}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Signature</label>
                <div>
                  {engineerSignatureUrl ? (
                      <img
                          width="200"
                          height="50"
                          style={{ border: "1px solid #ced4da", objectFit: "contain" }}
                          src={`${engineerSignatureUrl}?${sasToken}`}
                          alt="Engineer Signature"
                      />
                  ) : (
                      <div
                          className="form-control d-flex align-items-center text-muted"
                          style={{
                            width: "200px",
                            height: "50px",
                            backgroundColor: "#f8f9fa",
                          }}
                      >
                        No signature available
                      </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 print-hide">
            {/* SiteCheckPersistentSubmittedBack: keep navigation available after submission. */}
          {isSubmitted && (
            <div className="mt-3 print-hide">
              <SiteCheckBackButton />
            </div>
          )}

          {!isSubmitted ? (
                <div className="d-flex justify-content-between align-items-start mt-3">
                  <SiteCheckBackButton />
                  <div className="d-flex flex-column align-items-end">
                    {isFormEditable && (
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={

                                isLoading ||
                                isGeneratingPDF ||
                                (showRiskAssessment && !actionRaised)
                            }
                        >
                          {isLoading ? 'Submitting...' : 'Submit Report'}
                        </button>
                    )}
                    <div className="mt-3" style={{ minWidth: "280px" }}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-bold me-3">Frequency</span>
                        <span className="form-control text-end" style={{ backgroundColor: "#f8f9fa", maxWidth: "165px" }}>
                          {repeatFrequency}
                        </span>
                      </div>
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="fw-bold me-3">Next Due Date</span>
                        <span className="form-control text-end" style={{ backgroundColor: "#f8f9fa", maxWidth: "165px" }}>
                          {nextInspectionDate ? dateFormat(nextInspectionDate) : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
            ) : (
                <div className="text-center">
                  {pendingHistoryRetry ? (
                    <div className="alert alert-warning mb-4">
                      <div className="fw-bold mb-2">
                        Report and PDF saved, but History has not been recorded.
                      </div>
                      <div className="mb-3">
                        Do not submit the inspection again. Retry only the History record below.
                      </div>
                      <button
                        type="button"
                        className="btn btn-warning"
                        disabled={isRetryingHistory}
                        onClick={retryPendingAirConditioningHistory}
                      >
                        {isRetryingHistory ? "Retrying History..." : "Retry History"}
                      </button>
                    </div>
                  ) : (
                    <div className="alert alert-success mb-4">
                      Report submitted successfully on {getUkLocalDate()}
                    </div>
                  )}
                </div>
            )}
          </div>
        </form>

        <style>{`
        .is-invalid {
          border-color: #dc3545 !important;
        }
        .invalid-feedback {
          display: block;
          width: 100%;
          margin-top: .25rem;
          font-size: .875em;
          color: #dc3545;
        }
        @media print {
          .print-hide {
            display: none !important;
          }
          body {
            padding: 0;
            margin: 0;
          }
          .container {
            max-width: 100%;
            padding: 0;
          }
        }
      `}</style>
      </div>
  );
};

const mapStateToProps = (state) => ({
  sites: state.site.sites,
  users: state.site.users,
  siteSelectedForGlobal: state.site.siteSelectedForGlobal,
  loggedInUserData: state.site.loggedInUserData,
});

export default connect(mapStateToProps, {
  getUsers,
})(AirConditioning);